import { Link } from 'react-router-dom';
import {
  TValidationSerialResponse,
  LOCATION_PLACE,
  ACQUISITION_TYPE,
  TValidationSerialLocation,
  TValidationSerialAcquisition
} from 'types/suscription/productExternal.types';

/**
 * Genera el mensaje de ubicación del serial
 * @param location - Información de ubicación del serial
 * @returns Mensaje de ubicación
 */
const getLocationMessage = (location?: TValidationSerialLocation | null): string => {
  if (!location) return `El serial ya existe.`;

  const { place, address, countryName, userName } = location;

  if (place === LOCATION_PLACE.USER) {
    if (!userName) return `El serial ya existe, se encuentra ubicado en un empleado.`;
    return `El serial ya existe, se encuentra ubicado en el empleado ${userName}.`;
  }

  if (place === LOCATION_PLACE.OFFICE) {
    if (!countryName) return `El serial ya existe, se encuentra ubicado en una oficina.`;
    return `El serial ya existe, se encuentra ubicado en la Oficina de ${countryName}.`;
  }

  if (place === LOCATION_PLACE.WAREHOUSE) {
    if (!countryName) return `El serial ya existe, se encuentra ubicado en una bodega.`;
    return `El serial ya existe, se encuentra ubicado en la Bodega de ${countryName}.`;
  }

  const otherLocation = address || countryName;
  if (otherLocation) return `El serial ya existe, se encuentra ubicado en ${otherLocation}.`;
  return `El serial ya existe.`;
};

/**
 * Genera el enlace a la orden
 * @param orderId - ID de la orden
 * @returns Componente Link a la orden
 */
const generateOrderLink = (orderId: number): React.ReactNode => {
  return (
    <Link target="_blank" to={`/order/details/${orderId}`} className="link">
      Orden N° {orderId}
    </Link>
  );
};

/**
 * Genera el enlace a la orden de stock
 * @param stockOrderId - ID de la orden de stock
 * @param stockOrderUuid - UUID de la orden de stock
 * @returns Componente Link a la orden de stock
 */
const generateStockOrderLink = (stockOrderId: number, stockOrderUuid: string): React.ReactNode => {
  return (
    <Link target="_blank" to={`/stock/details/${stockOrderUuid}`} className="link">
      Orden de Stock N° {stockOrderId}
    </Link>
  );
};

/**
 * Genera el mensaje de adquisición basado en el tipo
 * @param type - Tipo de adquisición
 * @param orderId - ID de la orden (opcional)
 * @returns Mensaje de adquisición o null
 */
const getAcquisitionMessage = (acquisition?: TValidationSerialAcquisition | null): React.ReactNode | null => {
  const { type, orderId, stockOrderId, stockOrderUuid } = acquisition || {};

  if (type === ACQUISITION_TYPE.SALE || type === ACQUISITION_TYPE.SUBSCRIPTION) {
    if (!orderId) return null;
    return <>El producto fue adquirido en la {generateOrderLink(orderId)}</>;
  }

  if (type === ACQUISITION_TYPE.STOCK_PRODUCT) {
    if (!stockOrderId || !stockOrderUuid) return null;
    return <>El producto fue adquirido en la {generateStockOrderLink(stockOrderId, stockOrderUuid)}</>;
  }

  if (type === ACQUISITION_TYPE.PRODUCT_EXTERNAL) {
    return <>El producto fue cargado manualmente.</>;
  }

  return null;
};

/**
 * Genera el mensaje completo de error de validación de serial
 * @param response - Respuesta de validación del serial
 * @returns Componente React con el mensaje de error completo
 */
export const generateSerialValidationMessage = (response: TValidationSerialResponse | string): React.ReactNode => {
  if (typeof response === 'string') return <>{response}</>;

  const { acquisition, location } = response;

  const locationMessage = getLocationMessage(location);

  if (!acquisition) return <>{locationMessage}</>;

  const acquisitionMessage = getAcquisitionMessage(acquisition);

  if (!acquisitionMessage) return <>{locationMessage}</>;

  return (
    <>
      {locationMessage} {acquisitionMessage}
    </>
  );
};
